import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@mantine/core';
import { IconCar, IconChevronRight, IconClock, IconRoute } from '@tabler/icons-react';
import { useRidesMatchesService, RIDES_SEX, type RidesMatchQuery, type RidesMatchResult, type RidesMatchView, type RidesRouteSummary } from '@actschurch/ccms-sdk';
import { riderMissingStep, partySize, type RiderSelection } from '@/store/ridesWizard';
import { photoRules, useCopy, visiblePhoto } from '@/lib/settings';
import { ridesKeys } from '@/lib/queryKeys';
import { formatTime } from '@/lib/format';
import { serviceTimesFor, campusById } from '@/lib/lookups';
import { StepHeader } from '@/components/ui/StepHeader';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Callout } from '@/components/ui/Callout';
import { PlateChip } from '@/components/rides/PlateChip';
import { PersonAvatar } from '@/components/rides/PersonAvatar';
import { carWithColour } from '@/components/flows/LiftCard';
import { useRiderFlow } from './_shared';

/** The matches query for the current wizard state (shared with the booking step, which reads it from the cache). */
export function riderMatchQuery(rider: RiderSelection): RidesMatchQuery {
  return {
    eventId: rider.eventId!,
    collectionLocationId: rider.collectionLocationId!,
    dropOffLocationId: rider.dropOffLocationId!,
    maleCount: rider.maleCount,
    femaleCount: rider.femaleCount,
    forMinor: rider.forMinor ?? undefined,
    serviceTimeId: rider.serviceTimeId ?? undefined,
  };
}

/** Plain notes for drivers the safety rules hid. Shown under the list, never for a group of one. */
function hiddenNotes(r: RidesMatchResult, size: number, t: (k: string, f?: string, v?: Record<string, string | number>) => string): string[] {
  const out: string[] = [];
  const one = (n: number, key: string) => t(`${key}_${n === 1 ? 'one' : 'many'}`, undefined, { count: n });
  if (r.hiddenFemaleDriverCount > 0) out.push(one(r.hiddenFemaleDriverCount, 'matches.hidden_female'));
  if (r.hiddenMaleDriverCount > 0) out.push(one(r.hiddenMaleDriverCount, 'matches.hidden_male'));
  if ((r.hiddenUnclearedDriverCount ?? 0) > 0) out.push(one(r.hiddenUnclearedDriverCount ?? 0, 'matches.hidden_clearance'));
  if (r.hiddenForSeatsCount > 0 && size > 1) out.push(one(r.hiddenForSeatsCount, 'matches.hidden_seats'));
  return out;
}

/** Find a ride, choose a driver: verified drivers for this trip, earliest first, each with a clear "Choose" button. */
export default function RideMatchesPage() {
  const navigate = useNavigate();
  const matchesSvc = useRidesMatchesService();
  const { t, settings } = useCopy();
  const f = useRiderFlow();
  const { rider, setRider, lookups, event, col, drop } = f;
  const showDriverPhotos = photoRules(settings).driverToRiders;
  const missing = riderMissingStep(rider, 'matches');
  const query = riderMatchQuery(rider);
  const matches = useQuery({ queryKey: ridesKeys.matches(query), queryFn: () => matchesSvc.find(query), staleTime: 15_000, enabled: !missing });

  if (missing) return <Navigate to={missing} replace />;

  const size = partySize(rider);
  const r = matches.data;
  const drivers = [...(r?.drivers ?? [])].sort((a, b) => (a.departureTime ?? '99').localeCompare(b.departureTime ?? '99'));
  const notes = r ? hiddenNotes(r, size, t) : [];
  const whoBack = rider.forMinor && !f.memberIsMinor ? '/ride/party?q=child-who' : '/ride/party';

  /** Use another route from the empty state (same event, maybe another service). */
  const applyRoute = (rt: RidesRouteSummary) => {
    const dropCampus = campusById(lookups.data, rt.dropOffLocationID);
    const st = rt.serviceTimeStart ? serviceTimesFor(dropCampus).find((s) => formatTime(s.startTime) === formatTime(rt.serviceTimeStart)) : undefined;
    setRider({ collectionLocationId: rt.collectionLocationID, dropOffLocationId: rt.dropOffLocationID, serviceTimeId: st?.serviceTimeID ?? null, chosenAvailabilityId: null });
    window.scrollTo(0, 0);
  };

  return (
    <div className="rides-main rides-flow">
      <StepHeader
        step={f.stepOf('driver')}
        total={f.total}
        title="Choose your driver"
        lede={drivers.length ? t('ride.matches_intro') : undefined}
        backTo={whoBack}
        chips={f.chips('driver')}
      />
      <div className="rides-stepbody">

      {matches.isLoading && <LoadingState label="Finding drivers" count={3} />}
      {matches.error && <ErrorState title="We couldn't load the drivers" error={matches.error} onRetry={() => matches.refetch()} retrying={matches.isFetching} />}

      {r && (
        <div className="rides-flow-stack">
          {drivers.length === 0 ? (
            <>
              <EmptyState
                icon={<IconCar size={28} />}
                title={`${col?.name ?? 'This campus'} to ${drop?.name ?? 'that campus'}`}
                action={<Button fullWidth variant="default" component={Link} to="/ride/route">Change my campuses</Button>}
                secondaryAction={<Button fullWidth variant="subtle" component={Link} to="/ride/event">Choose another service</Button>}
              >
                {t('matches.empty')}
              </EmptyState>
              {r.availableRoutes.length > 0 && (
                <section>
                  <h2 className="rides-sectlabel" style={{ marginTop: 8 }}>Other trips for {event?.name ?? 'this service'}</h2>
                  <p className="rides-help" style={{ marginBottom: 10 }}>{t('matches.other_routes')}</p>
                  <div className="rides-stack">
                    {r.availableRoutes.map((rt) => (
                      <button key={`${rt.collectionLocationID}-${rt.dropOffLocationID}-${rt.serviceTimeStart ?? ''}`} type="button" className="rides-option" onClick={() => applyRoute(rt)}>
                        <span className="rides-icontile" aria-hidden="true"><IconRoute size={22} /></span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <b style={{ display: 'block', fontSize: 17 }}>{rt.collectionName} to {rt.dropOffName}</b>
                          <span style={{ display: 'block', color: 'var(--rides-muted)', fontSize: 15 }}>
                            {rt.serviceTimeStart ? `${formatTime(rt.serviceTimeStart)} service. ` : ''}
                            {rt.driverCount === 1 ? '1 driver' : `${rt.driverCount} drivers`}, {rt.seatsOpen === 1 ? '1 seat free' : `${rt.seatsOpen} seats free`}
                          </span>
                        </span>
                        <span className="rides-chev" aria-hidden="true"><IconChevronRight size={22} /></span>
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </>
          ) : (
            <div className="rides-stack" role="list" aria-label="Drivers">
              {drivers.map((d) => (
                <DriverCard
                  key={d.rideAvailabilityID}
                  d={d}
                  photoUrl={visiblePhoto(d.driverPhotoUrl, showDriverPhotos)}
                  usesServiceTimes={!!event?.usesCampusServiceTimes}
                  onPick={() => { setRider({ chosenAvailabilityId: d.rideAvailabilityID }); navigate('/ride/verify'); }}
                />
              ))}
            </div>
          )}

          {notes.length > 0 && (
            <Callout
              tone="info"
              title={drivers.length ? 'Why some drivers are not shown' : 'Why no drivers are shown'}
              action={
                r.hiddenFemaleDriverCount > 0 || r.hiddenForSeatsCount > 0 ? (
                  <button type="button" className="rides-linkbtn" onClick={() => navigate(whoBack)}>Change who's travelling</button>
                ) : undefined
              }
            >
              {notes.map((n) => <span key={n} style={{ display: 'block' }}>{n}</span>)}
            </Callout>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

function DriverCard({ d, photoUrl, usesServiceTimes, onPick }: { d: RidesMatchView; photoUrl: string | null; usesServiceTimes: boolean; onPick: () => void }) {
  const first = d.driverName.split(' ')[0];
  const when = d.departureTime ? `Leaves ${formatTime(d.departureTime)}` : 'Time to be confirmed';
  const svc = usesServiceTimes && d.serviceTimeStart ? `for the ${formatTime(d.serviceTimeStart)} service` : '';
  const seats = d.seatsOpen === 1 ? '1 seat' : `${d.seatsOpen} seats`;
  return (
    <div role="listitem">
      <button
        type="button"
        className="rides-option rides-match"
        onClick={onPick}
        aria-label={`Choose ${d.driverName}. ${carWithColour(d.vehicle)}, number plate ${d.vehicle.plate}. ${when}${svc ? ` ${svc}` : ''}. ${seats} free.`}
      >
        <span className="top">
          <PersonAvatar src={photoUrl} name={d.driverName} size={56} color={d.driverSexID === RIDES_SEX.Female ? 'pink' : 'brand'} decorative />
          <span className="who">
            <b>{d.driverName}</b>
            <span className="car">{carWithColour(d.vehicle)}</span>
            <PlateChip plate={d.vehicle.plate} />
          </span>
          <span className="seats">{seats}<small>free</small></span>
        </span>
        <span className="when">
          <IconClock size={18} aria-hidden="true" />
          <span>{when}{svc ? ` ${svc}` : ''}</span>
        </span>
        {d.notes?.trim() && <span className="note">“{d.notes.trim()}”</span>}
        <span className="go" aria-hidden="true">Choose {first} <IconChevronRight size={18} /></span>
      </button>
    </div>
  );
}
